/**
 * Sandbox Detail Page
 *
 * A Flow-Designer-style workspace: a large canvas (the setup checklist coming
 * online → a full-bleed agent chat once the sandbox is up) under a compact
 * flushed header, beside a 380px right panel with Overview / Checklist /
 * Diagnostics tabs. Logs live in a slide-up overlay off the header. Composition
 * root only — heavy lifting lives in the dedicated components in ../components/.
 *
 * @module features/sandboxes/pages/SandboxDetailPage
 */

import React from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  DocsAnnotation,
  Page,
  PageHeader,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import {
  useDeleteTemplate,
  useEnvironmentTemplate,
} from '../../../hooks/useEnvironmentTemplates';
import {
  useDeleteInstance,
  useEnvironmentInstance,
  useTerminateInstance,
} from '../../../hooks/useEnvironmentInstances';
import { ConnectLocallyDrawer } from '../components/ConnectLocallyDrawer';
import { SandboxCanvas } from '../components/SandboxCanvas';
import { SandboxCanvasHeader } from '../components/SandboxCanvasHeader';
import { SandboxLogsOverlay } from '../components/SandboxLogsOverlay';
import { SandboxSettingsOverlay } from '../components/SandboxSettingsOverlay';
import { SandboxSidebar, type SandboxSidebarTab } from '../components/SandboxSidebar';

/**
 * The route renders `<SandboxDetailPage />`, which keys `<SandboxDetailView />`
 * by `sandboxId` so every navigation between `/sandboxes/A` → `/sandboxes/B`
 * remounts the view with a clean slate. TanStack Router otherwise keeps the
 * same component instance mounted across param changes, which leaks local
 * state (dialog toggles, refs) across distinct sandboxes.
 */
export const SandboxDetailPage: React.FC = () => {
  const { sandboxId } = useParams({ from: '/sandboxes/$sandboxId' });
  return <SandboxDetailView key={sandboxId} sandboxId={sandboxId} />;
};

const SandboxDetailView: React.FC<{ sandboxId: string }> = ({ sandboxId }) => {
  const { tab: rawTab } = useSearch({ from: '/sandboxes/$sandboxId' });
  const tab = (rawTab ?? 'overview') as SandboxSidebarTab;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const chatReloadRef = React.useRef<(() => void) | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  // Feature gate
  React.useEffect(() => {
    if (user && !user.features?.environments) {
      void navigate({ to: '/' });
    }
  }, [user, navigate]);

  // `sandboxId` is the instance ID. Look it up first so we can derive the
  // template from `instance.templateId`. The instance endpoint embeds enough
  // template metadata for the header, but we still fetch the full template
  // because the actions/config menus need the editable template object.
  const instanceQuery = useEnvironmentInstance(sandboxId);
  const activeInstance = instanceQuery.data?.data ?? null;

  const templateQuery = useEnvironmentTemplate(activeInstance?.templateId ?? null);
  const template = templateQuery.data?.data ?? null;

  const deleteTemplateMutation = useDeleteTemplate();
  const terminateMutation = useTerminateInstance();
  const deleteInstanceMutation = useDeleteInstance();

  useBreadcrumbs([
    {
      key: 'sandboxes',
      label: 'Test Environments',
      onPress: () => navigate({ to: '/sandboxes' }),
    },
    {
      key: `sandbox-${sandboxId}`,
      label: template?.name ?? 'Loading...',
    },
  ]);

  const setTab = React.useCallback(
    (next: SandboxSidebarTab) => {
      void navigate({
        to: '/sandboxes/$sandboxId',
        params: { sandboxId },
        search: { tab: next },
        replace: true,
      });
    },
    [navigate, sandboxId]
  );

  const handleConnectLocally = React.useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleTerminate = async () => {
    if (!activeInstance) return;
    try {
      await terminateMutation.mutateAsync(activeInstance.id);
      toast.success('Termination started');
      setTerminateDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to terminate');
    }
  };

  const handleDelete = async () => {
    if (!template) return;
    try {
      if (activeInstance) {
        await deleteInstanceMutation.mutateAsync(activeInstance.id);
      }
      await deleteTemplateMutation.mutateAsync(template.id);
      toast.success('Sandbox deleted');
      setDeleteDialogOpen(false);
      void navigate({ to: '/sandboxes' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleViewTemplate = React.useCallback(() => {
    if (!activeInstance) return;
    void navigate({
      to: '/sandboxes/templates/$templateId',
      params: { templateId: activeInstance.templateId },
    });
  }, [navigate, activeInstance]);

  const handleCopyTemplateId = React.useCallback(() => {
    if (!template) return;
    void navigator.clipboard.writeText(template.id).then(
      () => toast.success('Template ID copied'),
      () => toast.error('Failed to copy — try again with the page focused')
    );
  }, [template, toast]);

  const handleCopyInstanceId = React.useCallback(() => {
    if (!activeInstance) return;
    void navigator.clipboard.writeText(activeInstance.id).then(
      () => toast.success('Instance ID copied'),
      () => toast.error('Failed to copy — try again with the page focused')
    );
  }, [activeInstance, toast]);

  if (!user?.features?.environments) {
    return null;
  }

  if (instanceQuery.isLoading || (activeInstance && templateQuery.isLoading)) {
    return (
      <Page>
        <div className="flex flex-col gap-4 px-8 py-6 mx-auto max-w-[1280px]">
          <div className="h-16 rounded-md bg-muted animate-pulse" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-40 rounded-md bg-muted animate-pulse" />
            <div className="h-40 rounded-md bg-muted animate-pulse" />
          </div>
        </div>
      </Page>
    );
  }

  if (!activeInstance || !template) {
    return (
      <Page>
        <PageHeader
          title="Sandbox Not Found"
          description="The requested sandbox could not be found"
        />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This sandbox may have been deleted or you may not have access.
          </Text>
          <Link
            to="/sandboxes"
            className="text-primary hover:underline text-sm block mt-4"
          >
            Back to sandboxes
          </Link>
        </div>
      </Page>
    );
  }

  // Only the instance's own status drives the failure takeover on the
  // instance detail page. Template `status='failed'` is a template-level
  // concern (shown on the TemplateDetailPage); it must not paint every
  // historical instance — including terminated and running ones — as failed,
  // which would make distinct sandboxes appear identical.
  const isFailed = activeInstance.status === 'failed';
  // Keep the setup takeover up through `running` until the first health check
  // lands, so the user sees the "Run health checks" step animate in.
  const isSettingUp =
    activeInstance.status === 'pending' ||
    activeInstance.status === 'provisioning' ||
    activeInstance.status === 'deploying' ||
    (activeInstance.status === 'running' && !activeInstance.lastHealthCheck);
  const runtimeAvailable = activeInstance.status === 'running';

  // The Checklist sidebar tab is the authoring setup checklist — only
  // authoring sandboxes own one. The canvas chat surface is gated on the
  // sandbox actually having an interactive agent: agentless running instances
  // still exist (verification sandboxes; the launch dialog defaults
  // `withAgent` off and the API stores `agentType: 'none'`), and must not hit
  // the sandbox-agent chat endpoints.
  const showChecklist = activeInstance.agentType === 'authoring';
  const hasChatAgent = activeInstance.agentType !== 'none';
  const isSuperadmin = user?.isSuperadmin === true;

  // If the URL points at the Checklist tab on a non-authoring sandbox, fall
  // back to Overview client-side. Overview + Diagnostics are always available
  // (Diagnostics gates only its live-metrics section internally).
  const effectiveTab: SandboxSidebarTab =
    tab === 'checklist' && !showChecklist ? 'overview' : tab;

  // FailedTakeover only renders when `isFailed` (instance status === 'failed'),
  // so the message always comes from the instance's terminationReason. The
  // template's verificationLogs would be wrong here because it's also written
  // on the success path (templates/author.job.ts:128).
  const failureMessage = activeInstance.terminationReason ?? null;

  // `handleDelete` runs the instance delete then the template delete in
  // sequence. Both phases must keep the dialog buttons disabled, otherwise
  // the user can click Cancel during the instance delete and watch the
  // template delete fire anyway, or click Delete twice and fire a redundant
  // instance delete.
  const isDeleting =
    deleteInstanceMutation.isPending || deleteTemplateMutation.isPending;

  return (
    <DocsAnnotation
      slug="reference/sandboxes"
      title="Test Environments"
      className="flex h-[calc(100vh-4rem)]"
    >
      {/* Canvas column — compact header, swapping body, slide-up logs overlay. */}
      <div className="relative flex flex-col overflow-hidden flex-1 min-w-0">
        <SandboxCanvasHeader
          template={template}
          activeInstance={activeInstance}
          onOpenLogs={() => setLogsOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onConnectLocally={handleConnectLocally}
          onTerminate={() => setTerminateDialogOpen(true)}
          onDelete={() => setDeleteDialogOpen(true)}
          onViewTemplate={handleViewTemplate}
          onCopyTemplateId={handleCopyTemplateId}
          onCopyInstanceId={handleCopyInstanceId}
        />
        <div className="flex-1 min-h-0">
          <SandboxCanvas
            instance={activeInstance}
            runtimeAvailable={runtimeAvailable}
            hasAgent={hasChatAgent}
            isSettingUp={isSettingUp}
            isFailed={isFailed}
            failureMessage={failureMessage}
            reloadRef={chatReloadRef}
            onRegenerate={handleViewTemplate}
            onOpenLogs={() => setLogsOpen(true)}
            onConnectLocally={handleConnectLocally}
          />
        </div>
        <AnimatePresence>
          {logsOpen && (
            <SandboxLogsOverlay
              isOpen={logsOpen}
              instanceId={activeInstance.id}
              onClose={() => setLogsOpen(false)}
            />
          )}
          {settingsOpen && (
            <SandboxSettingsOverlay
              isOpen={settingsOpen}
              instance={activeInstance}
              isSuperadmin={isSuperadmin}
              onClose={() => setSettingsOpen(false)}
            />
          )}
        </AnimatePresence>
      </div>

      <SandboxSidebar
        instance={activeInstance}
        templateId={activeInstance.templateId}
        runtimeAvailable={runtimeAvailable}
        showChecklist={showChecklist}
        activeTab={effectiveTab}
        onTabChange={setTab}
      />

      <ConnectLocallyDrawer
        isOpen={drawerOpen}
        onOpenChange={setDrawerOpen}
        instanceId={activeInstance.id}
      />

      {/* Terminate confirmation */}
      <DialogTrigger
        isOpen={terminateDialogOpen}
        onOpenChange={setTerminateDialogOpen}
        size="sm"
      >
        <span />
        <Dialog title="Terminate sandbox?">
          <DialogContent>
            <Text>
              This stops the running environment and releases its resources. You
              can spin up another instance from the template afterwards.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setTerminateDialogOpen(false)}
              isDisabled={terminateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={handleTerminate}
              isDisabled={terminateMutation.isPending}
            >
              {terminateMutation.isPending ? 'Terminating…' : 'Terminate'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Delete confirmation */}
      <DialogTrigger
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        size="sm"
      >
        <span />
        <Dialog title="Delete sandbox?">
          <DialogContent>
            <Text>
              This permanently deletes &quot;{template.name}&quot;
              {activeInstance ? ' and terminates its running instance' : ''}. This action
              cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setDeleteDialogOpen(false)}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={handleDelete}
              isDisabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete sandbox'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </DocsAnnotation>
  );
};
